import { asInt } from './canonical';
import type {
  ErrorListItem,
  PalletRow,
  ValidationErrors,
  ValidationResult,
  WarehouseState,
} from './types';

/** The warehouses the form has switched on, in the order the API returned them. */
export const selectEnabledWarehouses = <T extends { id: string }>(
  warehouses: T[],
  whState: WarehouseState
): T[] => (warehouses || []).filter((w) => whState[w.id]?.enabled);

/** A pallet row is usable once it has all three dimensions. Weight is optional. */
export const isPalletRowComplete = (r: PalletRow): boolean =>
  asInt(r.lengthIn) != null && asInt(r.widthIn) != null && asInt(r.heightIn) != null;

export const validateProductForm = (input: {
  name: string;
  imagePreview: string;
  deliveryType: string;
  defaultLocationId: string;
  enabledWarehouses: Array<{ id: string }>;
  palletRows: PalletRow[];
}): ValidationResult => {
  const { name, imagePreview, deliveryType, defaultLocationId, enabledWarehouses, palletRows } =
    input;

  const errors: ValidationErrors = {
    name: !name.trim(),
    image: !imagePreview,
    warehouses: enabledWarehouses.length === 0,
    // Only ask for a default once there is something to choose between, and
    // reject a default pointing at a warehouse that has since been switched off.
    defaultLocation:
      enabledWarehouses.length > 0 &&
      (!defaultLocationId || !enabledWarehouses.some((w) => w.id === defaultLocationId)),
    pallets: false,
  };

  if (deliveryType === 'PALLET') {
    errors.pallets = !palletRows.some(isPalletRowComplete);
  }

  const isValid = Object.values(errors).every((v) => v === false);
  return { errors, isValid };
};

/**
 * The error-summary banner. Order is the order the sections appear on the page,
 * because the submit handler scrolls to the first entry.
 */
export const buildErrorList = (errors: ValidationErrors, deliveryType: string): ErrorListItem[] => {
  const list: ErrorListItem[] = [];

  if (errors.name) {
    list.push({
      id: 'section-details-name',
      scrollTo: 'section-details',
      text: 'Product name is required.',
    });
  }

  if (errors.image) {
    list.push({
      id: 'section-details-image',
      scrollTo: 'section-details',
      text: 'A product image is required.',
    });
  }

  if (errors.warehouses) {
    list.push({
      id: 'section-warehouses',
      text: 'Select at least one warehouse.',
    });
  }

  if (errors.defaultLocation) {
    list.push({
      id: 'section-warehouses',
      text: 'Select a default warehouse.',
    });
  }

  if (deliveryType === 'PALLET' && errors.pallets) {
    list.push({
      id: 'section-pallets',
      text: 'Add at least one pallet size with length, width, height.',
    });
  }

  return list;
};
